import React, { useEffect, useState } from "react";
import { generatePath, NavLink } from "react-router-dom";
import { NavHashLink } from "react-router-hash-link";
import { useTranslation } from "react-i18next";
import { Dropdown, MenuProps } from "antd";

import { DialogWrapper } from "components/containers";
import { Button, TextPlain, TitleH2 } from "components/generic";
import { IHouseWithRentPeriod } from "interfaces/interfaces";
import {
  HOST_HOUSE_EDIT_ROUTE,
  HOST_HOUSE_VIEW_ROUTE,
  HOST_ROOM_REPUBLISH_ROUTE
} from "utils/constants";
import { useContextHospi } from "context/ContextHospi";
import Modal from "components/Modal";
import { DialogPublish } from "pages/HostHouseRegistration/DialogPublish";
import { getHouseName } from "../../../utils/house";
import { usePromisesLoader } from "../../../utils/usePromiseLoader";
import {
  LoadingIndicator,
  LoadingIndicatorContainer
} from "../../../components/LoadingIndicator";
import {
  useDeleteHouse,
  useTogglePublishing
} from "../../../API/queries/houseApiQueries";
import { useIsMobile } from "../../../utils/hooks/useWindowSize";

import {
  PublishButton,
  TogglePublish,
  WrapperContainerNew,
  RoomOverViewContainer,
  Image,
  DropdownIcon,
  AvailabilityHouse,
  AvailabilityHouseText,
  HouseTitle,
  HouseVisable
} from "./styles";

export const RoomCard: React.FC<{
  house: IHouseWithRentPeriod;
}> = ({ house }) => {
  const { t, i18n } = useTranslation("hostrooms");
  const { userProfile } = useContextHospi();
  const toggleHousePublish = useTogglePublishing();
  const deleteHouse = useDeleteHouse();
  const [publishChecked, setPublishChecked] = useState(
    house.house_status === "PUBLISHED"
  );
  const [showModal, setShowModal] = useState<null | "delete" | "publish">(null);
  const isMobile = useIsMobile();
  const { isLoading: isLoadingAction, watchPromise: watchPromiseAction } =
    usePromisesLoader();
  const dateLine = (value: string | undefined): string => {
    if (!value) return "";
    const dt = new Date(value);
    return `${("0" + dt.getDate()).slice(-2)}-${(
      "0" +
      (dt.getMonth() + 1)
    ).slice(-2)}-${dt.getFullYear()}`;
  };

  const removeRoom = () => {
    if (house.house_id) {
      watchPromiseAction(
        deleteHouse.mutateAsync({
          id: house.house_id
        })
      );
      setShowModal(null);
    }
  };

  const handleRequestPublish = (isOk: boolean) => {
    if (isOk) {
      watchPromiseAction(
        toggleHousePublish.mutateAsync({ id: house.house_id! })
      );
      setPublishChecked(true);
      window.dataLayer.push({
        event: "publish_room_success",
        user_id: userProfile?.user_id,
        user_type: "host"
      });
    } else {
      setPublishChecked(false);
    }
    setShowModal(null);
  };

  useEffect(() => {
    setPublishChecked(house.house_status === "PUBLISHED");
  }, [house.house_status]);

  const changePublishStatus = () => {
    console.log("House before publish", house);
    if (house.published_at) {
      watchPromiseAction(
        toggleHousePublish.mutateAsync({ id: house.house_id! })
      );
      setPublishChecked(false);
      window.dataLayer.push({
        event: "depublish_room",
        user_id: userProfile?.user_id,
        house_id: house.house_id,
        user_type: "host"
      });
    } else {
      setShowModal("publish");
      window.dataLayer.push({
        event: "publish_room",
        user_id: userProfile?.user_id,
        house_id: house.house_id,
        user_type: "host"
      });
    }
  };

  const handleCloseModal = () => {
    setPublishChecked(house.house_status === "PUBLISHED");
    setShowModal(null);
  };

  const viewPath = generatePath(HOST_HOUSE_VIEW_ROUTE, {
    lng: i18n.language,
    id: house.house_id!
  });

  const editPath = generatePath(HOST_HOUSE_EDIT_ROUTE, {
    lng: i18n.language,
    id: house.house_id!
  });

  const items: MenuProps["items"] = [
    {
      key: "view",
      label: <NavLink to={viewPath}>{t("hostRoomOverview.view_house")}</NavLink>
    },
    {
      key: "edit",
      label: <NavLink to={editPath}>{t("hostRoomOverview.edit_house")}</NavLink>
    }
  ];

  if (house.house_status !== "RENTED") {
    items.push({
      key: "delete",
      label: (
        <span onClick={() => setShowModal("delete")}>
          {t("hostRoomOverview.delete_house")}
        </span>
      )
    });
  }

  return (
    <RoomOverViewContainer>
      <WrapperContainerNew
        className="overview-row"
        isPublished={house.house_status === "PUBLISHED"}
      >
        <div className="block info-block">
          <div className="name-dropdown-wrapper">
            <NavLink to={viewPath}>
              <HouseTitle>
                {getHouseName(house.kind!, {
                  first_name: userProfile?.first_name!,
                  last_name: userProfile?.last_name!
                })}
              </HouseTitle>
            </NavLink>
            {isMobile ? (
              <Dropdown className="dropdown" menu={{ items: items }}>
                <DropdownIcon />
              </Dropdown>
            ) : null}
          </div>
          <AvailabilityHouseText>
            {t("hostRoomOverview.availability")}
          </AvailabilityHouseText>
          {house.house_status !== "RENTED" && (
            <AvailabilityHouse>{`${t(
              "availabilityPrices.items.extraDate.from"
            )} ${dateLine(house.available_from)} ${t(
              "availabilityPrices.items.extraDate.until"
            )} ${
              house.available_to === null
                ? t("anyDate").toLowerCase()
                : dateLine(house.available_to)
            }`}</AvailabilityHouse>
          )}
        </div>

        <div className="block image-block">
          {house.photos && house.photos.length > 0 ? (
            <NavLink to={viewPath}>
              <Image src={house.photos[0].url} key={1} />
            </NavLink>
          ) : null}
        </div>
        <div className="block publish-block">
          <PublishButton>
            <div className="top-row">
              <div className="publish-group">
                {publishChecked
                  ? t("preferredTenat.buttonUnPublish")
                  : t("preferredTenat.buttonPublish")}

                {house.house_status === "RENTED" ? (
                  <NavHashLink
                    to={generatePath(HOST_ROOM_REPUBLISH_ROUTE, {
                      lng: i18n.language,
                      id: house.house_id!
                    })}
                  >
                    <TogglePublish />
                  </NavHashLink>
                ) : (
                  <label>
                    <input
                      type="checkbox"
                      checked={publishChecked}
                      onChange={() => {
                        setPublishChecked((prev) => !prev);
                        changePublishStatus();
                      }}
                    />
                    <div className="slider round" />
                  </label>
                )}
              </div>
              {!isMobile ? (
                <Dropdown className="dropdown" menu={{ items: items }}>
                  <DropdownIcon />
                </Dropdown>
              ) : null}
            </div>
            {house.house_status === "PUBLISHED" ? (
              <HouseVisable className="bottom-row">
                {t("hostRoomOverview.published_state")}
              </HouseVisable>
            ) : null}
          </PublishButton>
        </div>
      </WrapperContainerNew>
      {showModal === "delete" ? (
        <Modal
          isShow={true}
          handleClose={() => {
            setShowModal(null);
            setPublishChecked(false);
          }}
        >
          <DialogWrapper>
            <TitleH2 className="titleDel">{t("deleteRoom.title")}</TitleH2>
            <TextPlain className="textDel">{t("deleteRoom.text")}</TextPlain>
            <div className="buttons">
              <Button
                normal
                onClick={() => setShowModal(null)}
                className="butCancel"
              >
                {t("deleteRoom.buttonCancel")}
              </Button>
              <Button danger onClick={removeRoom} className="butDel">
                {t("deleteRoom.buttonDelete")}
              </Button>
            </div>
          </DialogWrapper>
        </Modal>
      ) : null}
      {showModal === "publish" ? (
        <Modal isShow={true} handleClose={() => handleCloseModal()}>
          <DialogPublish
            requestPublish={handleRequestPublish}
            isVerified={userProfile?.verification_status === "verified"}
            isProfileCompleted={userProfile?.is_profile_complete ?? false}
            notProvidedFields={userProfile?.not_provided_fields ?? []}
          />
        </Modal>
      ) : null}
      {isLoadingAction ? (
        <LoadingIndicatorContainer>
          <LoadingIndicator />
        </LoadingIndicatorContainer>
      ) : null}
    </RoomOverViewContainer>
  );
};
